<script lang="ts" setup>
const { $directus, $readItems } = useNuxtApp()
const currentPage = ref(1)
const perPage = 4

const { locale, t } = useI18n()
const { dt } = useDirectusTranslation()

interface BlogData {
  count: number
  page: number
  data: {
    id: string
    slug: string
    date_created: string
    teaser_image: string
    translations: DirectusTranslation<{
      title: string
      description: string
    }>
  }[]
}

const { data, pending: _pending } = await useAsyncData<BlogData>(
  'blog_index',
  async () => {
    const page = currentPage.value
    const dataPromise = $directus.request($readItems('blogposts', {
      page,
      limit: perPage,
      sort: '-date_created',
      filter: {
        status: {
          _eq: 'published',
        },
      },
      fields: [
        'id',
        'slug',
        'date_created',
        'teaser_image',
        {
          translations: ['*'],
        },
      ],
    }))
    const countPromise = $directus.request($readItems('blogposts', {
      filter: {
        status: {
          _eq: 'published',
        },
      },
      aggregate: {
        count: '*',
      },
    }))
    const [data, count] = await Promise.all([dataPromise, countPromise])
    return {
      count: Number.parseInt(count[0]?.count),
      page,
      data,
    } as any
  },
  {
    watch: [currentPage],
  },
)

const results = useState<BlogData['data']>('blogIndexResults', () => data.value?.data ?? [])
watch(data, (value) => {
  if (!value?.data)
    return
  if (value.page === 1) {
    results.value = value.data
    return
  }
  results.value = [...results.value, ...value.data]
})
</script>

<template>
  <BlockBase>
    <div class="mb-6rem mt-8rem">
      <div v-auto-animate class="flex flex-col">
        <div
          v-for="blog in results"
          :key="blog.id"
        >
          <NuxtLinkLocale
            :to="`/blog/${blog.slug}`"
            class="group block py-2rem transition duration-300 hover:bg-neutral-white/10"
          >
            <Container class="grid grid-cols-1 items-center gap-2rem px-2rem lg:grid-cols-[1fr_min-content] lg:gap-6rem">
              <div class="grid grid-cols-1 mb-auto gap-2rem lg:grid-cols-[min-content_1fr] lg:gap-6rem">
                <div class="whitespace-nowrap text-1.1rem lg:mt-1.3rem">
                  {{ new Date(blog.date_created).toLocaleDateString(locale, {
                    dateStyle: 'short',
                  }) }}
                </div>
                <div>
                  <h2 class="mb-2rem text-balance text-2.5rem font-title">
                    {{ dt(blog.translations)?.title }}
                  </h2>
                  <p class="text-1.6rem">
                    {{ dt(blog.translations)?.description }}
                  </p>
                  <div class="mt-2rem flex items-center gap-1.2rem py-1rem text-1.3rem tracking-4px uppercase">
                    <div>{{ t('blog.read_article') }}</div>
                    <IconArrowRight class="size-2.5rem transition-300 group-hover:translate-x-1rem" />
                  </div>
                </div>
              </div>
              <div class="order-first h-full max-w-45rem w-full lg:order-none lg-w-45rem">
                <NuxtImg
                  loading="lazy"
                  draggable="false" class="h-full w-full" height="330" width="450" provider="directus" :src="blog.teaser_image"
                />
              </div>
            </Container>
          </NuxtLinkLocale>

          <Container>
            <div class="my-2rem h-px w-full bg-neutral-darkGray" />
          </Container>
        </div>

        <div v-if="results.length < (data?.count ?? Infinity)" class="mt-2rem w-full flex flex-col gap-2rem">
          <div class="text-center text-1.2rem text-neutral-white">
            {{ t('blog.index_counter', { number: results.length, total: data?.count }) }}
          </div>
          <button class="mx-auto btnPrimary" @click="currentPage++">
            {{ t('blog.load_more') }}
          </button>
        </div>
      </div>
    </div>
  </BlockBase>
</template>

<style scoped>

</style>
